import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { ISelectInputState } from './interface/uiState';
import {
  AggregateBusinessFilterProperty,
  FilterType,
  ForecastTimelineFilterProperty,
  isFetchRequiredFilterType,
} from '@/coreDomainsModule/entities/filter';
import { FilterModalType } from '../filterModal/filterModal';
import { initPagingOptions } from '@/coreDomainsModule/paging';
import { diContainer } from '@/coreContainer/container';

/**
 * 確定発注画面のStoreを返す
 * NOTE: Storeは同一の名前でSingletonとして扱われるため、名前を変える必要がある
 * @param filterType - フィルターキー
 * @param filterModalType - フィルターモーダルのタイプ
 * @param index - インデックス
 */
export const useSelectInputStore = (filterType: FilterType, filterModalType: FilterModalType, index = 1) =>
  defineStore(`useSelectInput-${filterType}-${index}`, () => {
    const state = reactive<ISelectInputState>({
      filterKey: filterType,
      options: [],
      pagingState: { type: 'none' },
      pagingOptions: initPagingOptions(15),
      searchText: '',
    });

    /**
     * stateの初期化
     */
    const $reset = () => {
      state.filterKey = filterType;
      state.options = [];
      state.pagingState = { type: 'none' };
      state.pagingOptions = initPagingOptions(15);
      state.searchText = '';
    };

    /**
     * フィルター情報をpropsからセットする
     * @param propOptions - propsから受け取った選択肢
     */
    const setFilterInfo = (propOptions: string[]) => {
      state.options = propOptions;
      state.pagingOptions = {
        pageSize: 1,
        offset: 0,
        totalResultsAvailable: propOptions.length,
        returnCount: propOptions.length,
      };
    };

    /**
     * フィルター情報を全てセットする
     * @param propAggregateBusinessFilterProperty - フィルター情報(集計取引用)
     * @param propCustomerForecastFilterProperty - フィルター情報(客先内示用)
     * @param propOptions - propsから受け取った選択肢
     */
    const fetchFilterInfo = async (
      propAggregateBusinessFilterProperty?: AggregateBusinessFilterProperty,
      propCustomerForecastFilterProperty?: ForecastTimelineFilterProperty,
      propOptions?: string[]
    ) => {
      if (!isFetchRequiredFilterType(state.filterKey)) {
        // フェッチが不要なフィルターの場合は何もしない
        return;
      }

      // optionsを初期化
      state.options = [];

      // NOTE: 現在はページングは不要なので、初期化している

      state.pagingOptions = initPagingOptions(0);

      const offset = state.pagingOptions.nextOffset ?? state.pagingOptions.offset;

      if (offset === 0) {
        // ファーストローディング
        state.pagingState = {
          type: 'loading',
        };
      } else if (state.pagingState.type === 'success') {
        state.pagingState = {
          type: 'success',
          footerLoadingState: 'loading',
          data: state.pagingState.data,
        };
      }

      // オフセットを更新
      state.pagingOptions = {
        ...state.pagingOptions,
        offset: offset,
      };

      const result = await diContainer.getFilterInfoUseCase.Execute(
        state.filterKey,
        state.searchText,
        filterModalType,
        propAggregateBusinessFilterProperty,
        propCustomerForecastFilterProperty,
        state.pagingOptions
      );
      let infos: string[] = [];
      switch (result.status.type) {
        case 'success':
          infos = result.status.data.data.concat(propOptions ?? []);
          state.pagingOptions = result.status.data.pagingOptions;
          if (infos.length === 0) {
            state.pagingState = {
              type: 'zeroMatch',
            };
          } else {
            if (offset === 0) {
              // ファーストローディング
              state.pagingState = {
                type: 'success',
                footerLoadingState: 'append',
                data: infos,
              };
            } else if (state.pagingState.type === 'success') {
              // 追加ローディング
              state.pagingState = {
                type: 'success',
                footerLoadingState: 'append',
                data: state.pagingState.data.concat(infos),
              };
            }
          }
          break;
        case 'error':
          if (offset === 0) {
            state.pagingState = {
              type: 'error',
              errorMessage: 'フィルター情報の取得に失敗しました', // TODO: APIからのエラーメッセージをハンドリング
            };
          } else if (state.pagingState.type === 'success') {
            state.pagingState = {
              type: 'success',
              footerLoadingState: 'error',
              data: state.pagingState.data,
            };
          }
          break;
      }
    };

    /**
     * ページングの終端に到達したかどうか
     */
    const endOfReached = () => {
      if (state.pagingState.type === 'success') {
        const pagingOptions = state.pagingOptions;
        const totalResultsAvailable = pagingOptions.totalResultsAvailable;
        const returnCount = pagingOptions.returnCount;
        const offset = pagingOptions.offset;
        if (!totalResultsAvailable || !returnCount) return false;
        return totalResultsAvailable <= offset + returnCount;
      } else {
        return false;
      }
    };

    /**
     * 検索処理
     * @param propAggregateBusinessFilterProperty - フィルター情報(集計取引用)
     * @param propCustomerForecastFilterProperty - フィルター情報(客先内示用)
     * @param propsOptions - propsから受け取った選択肢
     */
    const searchItem = async (
      propAggregateBusinessFilterProperty?: AggregateBusinessFilterProperty,
      propCustomerForecastFilterProperty?: ForecastTimelineFilterProperty,
      propsOptions?: string[]
    ) => {
      const keyword = state.searchText.trim().toLowerCase();
      const propsOptionsSearched = propsOptions?.filter((option) => option.toLowerCase().includes(keyword));
      if (!isFetchRequiredFilterType(state.filterKey)) {
        state.options = propsOptionsSearched ?? [];
        state.pagingState.type = state.options.length > 0 ? 'success' : 'zeroMatch';
      }
      await fetchFilterInfo(propAggregateBusinessFilterProperty, propCustomerForecastFilterProperty, propsOptionsSearched);
    };

    return {
      state,
      $reset,
      setFilterInfo,
      fetchFilterInfo,
      endOfReached,
      searchItem,
    };
  });
